/**
 * The calendars this account has connected.
 *
 * **Personal, not organisational**, which is why it lives on the account page
 * rather than under Settings. A calendar belongs to one person; nobody else in
 * the tenant can connect it for them, and nobody else's is visible here.
 *
 * It is also the setting the product depends on most: with no calendar there
 * are no upcoming meetings, so nothing gets recorded automatically and the
 * Meetings tab has only what was uploaded or sent by hand.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Calendar, Check, Loader2, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/display'
import {
  calendarConnectUrl,
  disconnectCalendar,
  getCalendars,
  type CalendarProvider,
} from '@/data/capture'
import { currentUserId } from '@/lib/api'
import { Consequence, SettingsCard } from '@/features/settings/SettingsCard'

const PROVIDERS: Array<{ id: CalendarProvider; label: string }> = [
  { id: 'microsoft', label: 'Microsoft 365' },
  { id: 'google', label: 'Google Calendar' },
]

export function Calendars() {
  const queryClient = useQueryClient()
  const userId = currentUserId()

  const calendars = useQuery({
    queryKey: ['calendars', userId],
    queryFn: () => getCalendars(userId),
  })

  /**
   * Connecting leaves the app.
   *
   * Consent happens at Microsoft or Google and returns to the service, so this
   * hands the browser off rather than awaiting anything — the pane finds out it
   * worked by asking again when the tab comes back.
   */
  const connect = useMutation({
    mutationFn: async (provider: CalendarProvider) => {
      window.location.href = await calendarConnectUrl(userId, provider)
    },
  })

  const disconnect = useMutation({
    mutationFn: (provider: CalendarProvider) => disconnectCalendar(userId, provider),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['calendars', userId] }),
  })

  const connected = calendars.data?.connections ?? []
  const connectedIds = new Set(connected.map((entry) => entry.provider))

  return (
    <SettingsCard title="Calendars" meta="how your meetings are found">
      {calendars.isLoading ? (
        <div className="flex items-center gap-2 py-2 text-xs text-[var(--color-fg-muted)]">
          <Loader2 className="size-3 animate-spin" />
          Checking…
        </div>
      ) : connected.length === 0 ? (
        <EmptyState
          title="No calendar connected."
          description="Without one, nothing is recorded automatically — meetings only arrive by uploading them or sending the recorder a link."
          className="py-6"
        />
      ) : (
        <ul className="divide-y divide-[var(--color-border-subtle)]">
          {connected.map((connection) => (
            <li key={connection.provider} className="flex items-center gap-2.5 py-2">
              <Calendar className="size-3.5 shrink-0 text-[var(--color-fg-subtle)]" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-xs text-[var(--color-fg)]">
                  {connection.email}
                </span>
                <span className="block text-2xs text-[var(--color-fg-subtle)]">
                  {PROVIDERS.find((entry) => entry.id === connection.provider)?.label ??
                    connection.provider}
                </span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={disconnect.isPending}
                onClick={() => disconnect.mutate(connection.provider)}
              >
                Disconnect
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        {PROVIDERS.map((provider) => (
          <Button
            key={provider.id}
            variant="secondary"
            size="sm"
            disabled={connect.isPending}
            onClick={() => connect.mutate(provider.id)}
          >
            {connectedIds.has(provider.id) ? (
              <Check className="size-3.5" />
            ) : (
              <Plus className="size-3.5" />
            )}
            {connectedIds.has(provider.id) ? `Reconnect ${provider.label}` : provider.label}
          </Button>
        ))}
      </div>

      {/* Reconnect stays available even when connected: a revoked or expired
          grant looks exactly like a working one from here, and re-consenting is
          the fix. */}
      {calendars.data?.connected ? (
        <Consequence>
          {calendars.data.upcoming} upcoming meeting
          {calendars.data.upcoming === 1 ? '' : 's'} visible, {calendars.data.scheduled} with a
          recorder booked. What gets recorded by default is the organisation's setting, above.
        </Consequence>
      ) : null}
    </SettingsCard>
  )
}
